import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faKey, faRightFromBracket, faUser } from '@fortawesome/free-solid-svg-icons';

function Profile() {
    const [user, setUser] = useState({
        first_name: "",
        last_name: "",
        phone_number: "",
        email: "",
        status: "",
        role: { name: "" },
    });
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const navigate = useNavigate();
    const userRole = localStorage.getItem('userRole');

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const response = await fetch(`${API_URL}/profile`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setUser(data);
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: "User profile not found." });
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    throw new Error("Failed to fetch user profile.");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching the user profile." });
            }
            setLoading(false);
        };

        fetchUserProfile();
    }, [navigate]);

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    if (loading) return <div className="container"><p className="has-text-centered py-6">Loading profile...</p></div>;

    return (
        <div className="container">
            <div className="section">
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <h1 className="title is-3">My Profile</h1>
                        </div>
                    </div>
                    <div className="level-right">
                        {userRole === 'admin' || userRole === 'superadmin' && (
                            <div className="level-item">
                                <Link to="/dashboard" className="button is-light">
                                    <span className="icon">
                                        <i className="fas fa-arrow-left"></i>
                                    </span>
                                    <span>Back</span>
                                </Link>

                                <Link to="/dashboard/profile/update" className="button is-primary ml-3">
                                    <span className="icon">
                                        <i className="fas fa-pencil"></i>
                                    </span>
                                    <span>Edit</span>
                                </Link>

                            </div>
                        )}
                    </div>
                </nav>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <div className="columns">
                    {/* Main Profile Card */}
                    <div className="column is-8">
                        <div className="card">
                            <div className="card-content">
                                <div className="media">
                                    <div className="media-left">
                                        <figure className="image is-96x96">
                                            <div className="is-flex is-justify-content-center is-align-items-center has-background-primary has-text-white is-size-2" style={{ width: '96px', height: '96px', borderRadius: '50%' }}>
                                                {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                                            </div>
                                        </figure>
                                    </div>
                                    <div className="media-content">
                                        <p className="title is-3">{user.first_name} {user.last_name}</p>
                                        <p className="subtitle is-5">
                                            <span className="tag is-info mr-2">{userRole?.toUpperCase()}</span>
                                            <span className={`tag ${user.status === "Active" ? "is-success" : "is-danger"}`}>
                                                {user.status?.toUpperCase()}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                <div className="content mt-5">
                                    <div className="columns">
                                        <div className="column">
                                            <div className="field is-horizontal">
                                                <div className="field-label is-normal">
                                                    <label className="label">Email</label>
                                                </div>
                                                <div className="field-body">
                                                    <div className="field">
                                                        <p className="control">
                                                            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                                            {user.email}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="field is-horizontal">
                                                <div className="field-label is-normal">
                                                    <label className="label">Phone</label>
                                                </div>
                                                <div className="field-body">
                                                    <div className="field">
                                                        <p className="control">
                                                            <FontAwesomeIcon icon={faPhone} className="mr-2" />
                                                            {user.phone_number}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="field is-horizontal">
                                                <div className="field-label is-normal">
                                                    <label className="label">User Name</label>
                                                </div>
                                                <div className="field-body">
                                                    <div className="field">
                                                        <p className="control">
                                                            <FontAwesomeIcon icon={faUser} className="mr-2" />
                                                            {user.username}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>


                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions Column */}
                    <div className="column is-4">
                        <div className="card">
                            <div className="card-content">
                                <h3 className="title is-5">Account Actions</h3>
                                <div className="buttons">
                                    <Link to="/dashboard/profile/reset-password" className="button is-fullwidth">
                                        <FontAwesomeIcon icon={faKey} className="mr-2" />
                                        Reset Password
                                    </Link>
                                    <Link to="/logout" className="button is-fullwidth">
                                        <FontAwesomeIcon icon={faRightFromBracket} className="mr-2" />
                                        Log Out
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <div className="card mt-4">
                            <div className="card-content">
                                <h3 className="title is-5">Account Details</h3>
                                <div className="content">
                                    <p>
                                        <strong>Created:</strong> {new Date(user.created_at).toLocaleDateString()}
                                    </p>
                                    <p>
                                        <strong>Last Updated:</strong> {new Date(user.updated_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Profile;